// --- UPDATED FILE: packages/components/src/admin/AdminSidebar.tsx ---

import React from 'react';
import { NavLink } from 'react-router-dom';
import { Icon, Logo, IconButton } from '@dopomogai/ui';
import type { IconName } from '@dopomogai/ui';
import clsx from 'clsx';

interface AdminNavItem {
  label: string;
  path: string;
  icon: IconName;
}

// --- NEW, LOGICALLY GROUPED NAV ITEMS ---
const navSections: { title: string; items: AdminNavItem[] }[] = [
  {
    title: 'Content',
    items: [
      { label: 'Projects', path: '/admin/projects', icon: 'package' },
      // --- NEW: Add link to the CMS dashboard ---
      { label: 'CMS', path: '/admin/cms', icon: 'layout-grid' }, 
      { label: 'Goals', path: '/admin/manage-goals', icon: 'target' },
    ]
  },
  {
    title: 'Platform',
    items: [
      { label: 'Users', path: '/admin/manage-users', icon: 'users' },
      { label: 'Finance', path: '/admin/finance', icon: 'trending-up' },
      { label: 'Transactions', path: '/admin/transactions', icon: 'boxes' },
      { label: 'Payments', path: '/admin/payments', icon: 'shopping-cart' },
    ]
  }
];

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ isCollapsed, onToggle }) => {
  return (
    <aside className={clsx("flex-shrink-0 bg-slate-900 text-slate-200 flex flex-col transition-all duration-300 ease-in-out", isCollapsed ? "w-20" : "w-64")}>
      {/* Header with Logo and Toggle Button */}
      <div className={clsx("h-20 flex items-center border-b border-slate-700 transition-all duration-300", isCollapsed ? "px-5" : "px-6")}>
        {!isCollapsed && <Logo logoType="logo+icon" size="sm" to="/" />}
        {isCollapsed && <Logo logoType="favicon" size="sm" to="/" />}
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 p-4 space-y-4">
        {navSections.map(section => (
          <div key={section.title}>
            {!isCollapsed && (
              <h4 className="px-3 pt-2 pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                {section.title}
              </h4>
            )}
            {section.items.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => clsx(
                    "flex items-center gap-3 p-3 rounded-lg text-sm font-semibold transition-colors duration-150",
                    isCollapsed ? 'justify-center' : '',
                    isActive ? 'bg-emerald-500 text-white' : 'hover:bg-slate-800 text-slate-300'
                )}
                title={isCollapsed ? item.label : undefined} // Show tooltip when collapsed
              >
                <Icon name={item.icon} className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      
      {/* Footer Toggle Button */}
      <div className="p-4 border-t border-slate-700">
        <IconButton
          icon={isCollapsed ? 'PanelRightOpen' : 'PanelLeftOpen'}
          onClick={onToggle}
          className="w-full text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          tooltip={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        />
      </div>
    </aside>
  );
};